import React from "react";
import {
  FileText,
  CheckCircle2,
  AlertCircle,
  HardDrive,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  color?: "default" | "success" | "warning" | "error";
  size?: "sm" | "md" | "lg";
}

function StatCard({
  icon: Icon,
  label,
  value,
  color = "default",
  size = "md",
}: StatCardProps) {
  const sizeClasses = {
    sm: "p-3",
    md: "p-4",
    lg: "p-5",
  };

  const iconSizes = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  const colorClasses = {
    default: "bg-primary/10 text-primary border-primary/20",
    success: "bg-green-500/10 text-green-600 border-green-500/20",
    warning: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
    error: "bg-red-500/10 text-red-600 border-red-500/20",
  };

  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          "flex items-center justify-center rounded-xl border",
          sizeClasses[size],
          colorClasses[color]
        )}
      >
        <Icon className={iconSizes[size]} />
      </div>
      <div className="space-y-1">
        <p className="text-2xl font-semibold text-[var(--foreground)]">
          {value}
        </p>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">
          {label}
        </p>
      </div>
    </div>
  );
}

interface DocumentStatsProps {
  totalDocuments: number;
  completedDocuments: number;
  processingDocuments: number;
  errorDocuments: number;
  totalSize?: number;
  className?: string;
}

export function DocumentStats({
  totalDocuments,
  completedDocuments,
  processingDocuments,
  errorDocuments,
  totalSize = 0,
  className,
}: DocumentStatsProps) {
  const formatStorageSize = (bytes: number): string => {
    if (bytes === 0) return "0 MB";
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const completionRate = totalDocuments > 0 
    ? Math.round((completedDocuments / totalDocuments) * 100) 
    : 0;

  return (
    <div className={cn("grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5", className)}>
      <StatCard
        icon={FileText}
        label="Total documentos"
        value={totalDocuments}
        color="default"
      />
      
      <StatCard
        icon={CheckCircle2}
        label="Completados"
        value={completedDocuments}
        color="success"
      />
      
      <StatCard
        icon={Loader2}
        label="Procesando"
        value={processingDocuments}
        color="warning"
      />
      
      <StatCard
        icon={AlertCircle}
        label="Con errores"
        value={errorDocuments}
        color="error"
      />
      
      <StatCard
        icon={HardDrive}
        label="Almacenamiento"
        value={formatStorageSize(totalSize)}
        color="default"
      />
      
      {/* Tarjeta de progreso general */}
      <div className="col-span-2 md:col-span-3 lg:col-span-5">
        <div className="rounded-xl border border-black/5 bg-[var(--card-background)] p-4 dark:border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[var(--foreground)]">
              Progreso general
            </span>
            <span className="text-sm font-bold text-primary">
              {completionRate}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500" 
              style={{ width: `${completionRate}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            {completedDocuments} de {totalDocuments} documentos procesados exitosamente
          </div>
        </div>
      </div>
    </div>
  );
}